import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const SUPPORT_COLLECTION = "support";

function now() {
  return Date.now();
}

export async function createSupportTicket(user, orgId, { category, subject, message } = {}) {
  if (!user?.uid || !orgId) return null;
  const payload = {
    orgId,
    category: category || "General",
    subject: subject || "Support request",
    status: "open",
    createdAt: now(),
    updatedAt: now(),
    userId: user.uid,
    userEmail: user.email || "",
    userName: user.username || user.email?.split("@")[0] || "User",
    messages: [
      {
        id: `${user.uid}_${now()}`,
        authorId: user.uid,
        authorRole: user.role || "member",
        authorEmail: user.email || "",
        message: message || "",
        createdAt: now(),
      },
    ],
  };
  try {
    const ref = await addDoc(collection(db, "orgs", orgId, SUPPORT_COLLECTION), payload);
    return { id: ref.id, ...payload };
  } catch {
    return null;
  }
}

export async function fetchSupportTickets(user, orgId, { status = "all", mineOnly = false, max = 50 } = {}) {
  if (!user?.uid || !orgId) return [];
  try {
    const base = collection(db, "orgs", orgId, SUPPORT_COLLECTION);
    const constraints = [];
    if (status && status !== "all") {
      constraints.push(where("status", "==", status));
    }
    if (mineOnly) {
      constraints.push(where("userId", "==", user.uid));
    }
    // Avoid composite index requirements by ordering client-side when filters are applied.
    const q = constraints.length
      ? query(base, ...constraints, limit(max))
      : query(base, orderBy("createdAt", "desc"), limit(max));
    const snap = await getDocs(q);
    const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    if (constraints.length) {
      return rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return rows;
  } catch {
    return [];
  }
}

export async function addSupportReply(user, orgId, ticketId, message, roleOverride) {
  if (!user?.uid || !orgId || !ticketId || !message) return false;
  try {
    const ref = doc(db, "orgs", orgId, SUPPORT_COLLECTION, ticketId);
    const entry = {
      id: `${user.uid}_${now()}`,
      authorId: user.uid,
      authorRole: roleOverride || user.role || "member",
      authorEmail: user.email || "",
      message,
      createdAt: now(),
    };
    await updateDoc(ref, {
      messages: arrayUnion(entry),
      updatedAt: now(),
      status: "open",
    });
    return true;
  } catch {
    return false;
  }
}

export async function resolveSupportTicket(user, orgId, ticketId, resolutionNote) {
  if (!user?.uid || !orgId || !ticketId) return false;
  try {
    const ref = doc(db, "orgs", orgId, SUPPORT_COLLECTION, ticketId);
    const updates = {
      status: "resolved",
      resolvedAt: now(),
      resolvedBy: user.uid,
      updatedAt: now(),
    };
    if (resolutionNote) {
      updates.messages = arrayUnion({
        id: `${user.uid}_${now()}`,
        authorId: user.uid,
        authorRole: user.role || "superadmin",
        authorEmail: user.email || "",
        message: resolutionNote,
        createdAt: now(),
      });
    }
    await updateDoc(ref, updates);
    return true;
  } catch {
    return false;
  }
}
